import React, { useMemo } from 'react';

/* External */
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

/* Material UI */
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import FormHelperText from '@material-ui/core/FormHelperText';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import Checkbox from '@material-ui/core/Checkbox';

/* Project */
import ButtonWithLoader from 'commons/containers/ButtonWithLoader';
import DatePicker from 'commons/components/DatePicker';
import { truthty, falsy } from 'utils/functions';
import { defaultProcesses } from 'utils/defaultProcesses';

function ProcessForm(props) {
  const { t } = useTranslation();
  const {
    toggleForm,
    isOpen,
    edit,
    controls,
    changeControls,
    invalidControls,
    processTypes,
    workShiftTypes,
    processCategories,
    submit,
    receiptLots,
  } = props;

  const categoryModules = useSelector((state) => state.modules.modules);

  const sortedCategories = useMemo(
    () => Object.values(processCategories).sort((a, b) => a.name.localeCompare(b.name)),
    [processCategories],
  );

  const sortedCategoryModules = useMemo(
    () => [...categoryModules].sort((a, b) => a.name.localeCompare(b.name)),
    [categoryModules],
  );

  const filteredCategoryModules = controls.processCategoryId
    ? sortedCategoryModules.filter(
        (module) =>
          module.categoryId === controls.processCategoryId || controls.processCategoryId === '',
      )
    : sortedCategoryModules;

  return (
    <Dialog fullWidth onClose={toggleForm} aria-labelledby="simple-dialog-title" open={isOpen}>
      <DialogTitle>
        {edit ? `${t('Edit')} ${t('Process').toLowerCase()}` : t('Create process')}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item sm={6} xs={12}>
            <DatePicker
              inputId="date"
              label={t('Date')}
              type="datetime-local"
              fullWidth
              value={controls.date}
              onChange={changeControls}
              inputProps={{ 'data-module': 'closeModal' }}
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <Autocomplete
              value={
                controls.processTypeId &&
                Object.values(processTypes).find(
                  (processType) => processType.id === controls.processTypeId,
                )
              }
              id="autocomplete-processType-processes"
              options={Object.values(processTypes)
                .filter((processType) => !defaultProcesses.includes(processType.name))
                .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))}
              getOptionLabel={(processType) => processType && processType.name}
              renderInput={(params) => (
                <TextField
                  style={{ margin: 0 }}
                  {...params}
                  label={t('Process type')}
                  margin="normal"
                />
              )}
              onChange={(event, newValue) => {
                const e = {
                  autocomplete: true,
                  input: 'processTypeId',
                  value: newValue ? newValue.id : '',
                };
                changeControls(e);
              }}
            />
            {truthty(invalidControls.processTypeId) && (
              <FormHelperText error>{t('Process type required')}</FormHelperText>
            )}
          </Grid>
          <Grid item sm={6} xs={12}>
            <FormControl fullWidth>
              <InputLabel id="process-categories-label">{t('Process category')}</InputLabel>
              <Select
                labelId="process-categories-label"
                fullWidth
                value={controls.processCategoryId}
                onChange={changeControls}
                error={invalidControls.processCategoryId}>
                <MenuItem id="processCategoryId" value="">
                  {t('None')}
                </MenuItem>
                {Object.values(sortedCategories).map((processCategory) => (
                  <MenuItem
                    key={`process-category-form-option-${processCategory.id}`}
                    id="processCategoryId"
                    value={processCategory.id}>
                    {processCategory.name}
                  </MenuItem>
                ))}
              </Select>
              {truthty(invalidControls.processCategoryId) && (
                <FormHelperText error>{t('Process category required')}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item sm={6} xs={12}>
            <FormControl fullWidth>
              <InputLabel id="work-shift-type-label">{t('Shift type')}</InputLabel>
              <Select
                labelId="work-shift-type-label"
                fullWidth
                value={controls.workShiftTypeId}
                onChange={changeControls}
                error={invalidControls.workShiftTypeId}>
                <MenuItem id="workShiftTypeId" value="">
                  {t('None')}
                </MenuItem>
                {Object.values(workShiftTypes).map((workShiftType) => (
                  <MenuItem
                    key={`work-shift-type-form-option-${workShiftType.id}`}
                    id="workShiftTypeId"
                    value={workShiftType.id}>
                    {workShiftType.name}
                  </MenuItem>
                ))}
              </Select>
              {truthty(invalidControls.workShiftTypeId) && (
                <FormHelperText error>{t('Shift type required')}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item sm={12} xs={12}>
            <Autocomplete
              multiple
              id="autocomplete-processes-modules"
              value={controls.processModules}
              options={filteredCategoryModules}
              getOptionLabel={(option) => option.name}
              getOptionSelected={(option, value) => option.id === value.id}
              renderOption={(option, { selected }) => (
                <>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.name}
                </>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  label={t('process.modules')}
                  placeholder={t('process.selectOneOrMoreModules')}
                />
              )}
              onChange={(event, newValue) => {
                const e = {
                  autocomplete: true,
                  input: 'processModules',
                  value: newValue || [],
                };

                changeControls(e);
              }}
            />
          </Grid>

          <Grid item sm={6} xs={12}>
            <Autocomplete
              value={
                controls.admitedLotNumber &&
                Object.values(receiptLots).find((rp) => rp.number === controls.admitedLotNumber)
              }
              disabled={
                edit ||
                falsy(controls.processTypeId) ||
                processTypes[controls.processTypeId].allowMultipleLots
              }
              id="autocomplete-admitedLotNumber-processes"
              options={Object.values(receiptLots).sort((a, b) => a.number - b.number)}
              getOptionLabel={(receiptLot) => receiptLot && receiptLot.number.toString()}
              renderInput={(params) => (
                <TextField
                  style={{ margin: 0 }}
                  {...params}
                  label={t('Optional lot')}
                  margin="normal"
                />
              )}
              onChange={(event, newValue) => {
                const e = {
                  autocomplete: true,
                  input: 'admitedLotNumber',
                  value: newValue ? newValue.number : '',
                  module: 'formControls',
                };
                changeControls(e);
              }}
            />
            {falsy(processTypes[controls.processTypeId]?.allowMultipleLots) &&
              controls.admitedLotNumber}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={toggleForm} color="primary" variant="outlined">
          {t('Cancel')}
        </Button>
        <ButtonWithLoader onClick={submit} autoFocus buttonId="create-edit-process">
          {edit ? t('Edit') : t('Add')}
        </ButtonWithLoader>
      </DialogActions>
    </Dialog>
  );
}

export default ProcessForm;
